import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Plus, Edit, Trash2, Search, Users, MapPin, UserCheck, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { userService, roleService, branchService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { canDelete, isSuperAdmin } from '../../utils/rolePermissions';

const EmployeeMaster = () => {
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBranchModal, setShowBranchModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [assigningBranchTo, setAssigningBranchTo] = useState(null);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm();

  useEffect(() => {
    loadEmployees();
    loadRoles();
    loadBranches();
  }, []);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const response = await userService.getAll({ search: searchTerm });
      setEmployees(response.data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await roleService.getAll();
      // Filter to only show the 3 operational roles
      const operationalRoles = ['Super Admin', 'Store Manager', 'Authenticator'];
      const filteredRoles = (response.data || []).filter(role => 
        operationalRoles.includes(role.name)
      );
      setRoles(filteredRoles);
    } catch (error) {
      console.error('Error loading roles:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const response = await branchService.getAll();
      setBranches(response.data || []);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadEmployees();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const openModal = (employee = null) => {
    setEditingEmployee(employee);
    setShowModal(true);
    if (employee) {
      reset({
        name: employee.name,
        email: employee.email,
        role_id: employee.role_id,
        is_active: employee.is_active ? 1 : 0
      });
    } else {
      reset({ 
        name: '',
        email: '',
        password: '',
        role_id: '',
        is_active: 1 
      });
    }
  };

  const openBranchModal = (employee) => {
    setAssigningBranchTo(employee);
    setSelectedBranches(employee.branches?.map(b => b.id) || []);
    setShowBranchModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingEmployee(null);
    reset();
  };

  const closeBranchModal = () => {
    setShowBranchModal(false);
    setAssigningBranchTo(null);
    setSelectedBranches([]);
  };

  const handleBranchToggle = (branchId) => {
    setSelectedBranches(prev => 
      prev.includes(branchId) 
        ? prev.filter(id => id !== branchId)
        : [...prev, branchId]
    );
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      const submitData = {
        name: data.name,
        email: data.email,
        role_id: parseInt(data.role_id),
        is_active: data.is_active === 1 || data.is_active === true
      };

      if (!editingEmployee && data.password) {
        submitData.password = data.password;
      }
      
      if (editingEmployee) {
        await userService.update(editingEmployee.id, submitData);
        toast.success('Employee updated successfully!');
      } else {
        await userService.create(submitData);
        toast.success('Employee created successfully!');
      }
      closeModal();
      loadEmployees();
    } catch (error) {
      console.error('Error saving employee:', error);
      toast.error('Failed to save employee');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignBranches = async () => {
    try {
      await userService.assignBranches(assigningBranchTo.id, {
        branch_ids: selectedBranches
      });
      toast.success('Branches assigned successfully!');
      closeBranchModal();
      loadEmployees();
    } catch (error) {
      console.error('Error assigning branches:', error);
      toast.error('Failed to assign branches');
    }
  };

  const deleteEmployee = async (id) => {
    if (isSuperAdmin(user)) {
      toast.error('Super Admin cannot delete records');
      return;
    }

    if (window.confirm('Are you sure you want to delete this employee?')) {
      try {
        setLoading(true);
        await userService.delete(id);
        toast.success('Employee deleted successfully!');
        loadEmployees();
      } catch (error) {
        console.error('Error deleting employee:', error);
        toast.error('Failed to delete employee');
      } finally {
        setLoading(false);
      }
    }
  };

  const toggleStatus = async (employee) => {
    try {
      setLoading(true);
      await userService.toggleStatus(employee.id);
      toast.success('Employee status updated!');
      loadEmployees();
    } catch (error) {
      console.error('Error updating employee status:', error);
      toast.error('Failed to update employee status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
                        <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
              <Users size={28} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              Employee Master
            </h1>
            <p style={{ 
              margin: '8px 0 0 0', 
              color: '#6b7280', 
              fontSize: '14px',
              maxWidth: '800px'
            }}>
              Manage employees and assign them to branches. Employees can only access data from their assigned branches.
            </p>
          </div>
          {isSuperAdmin(user) && (
            <button
              onClick={() => openModal()}
              className="btn btn-primary"
            >
              <Plus size={16} style={{ marginRight: '8px' }} />
              Add Employee
            </button>
          )}
        </div>

        {/* Search */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative' }}>
            <Search size={20} style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: '#6b7280'
            }} />
            <input
              type="text"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        {/* Table */}
        <div style={{
          overflowX: 'auto',
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>Name</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>Email</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>Role</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>Assigned Branches</th>
                <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#111827' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#111827' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px' }}>{employee.name}</td>
                  <td style={{ padding: '12px' }}>{employee.email}</td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: '#dbeafe',
                      color: '#1e40af'
                    }}>
                      {employee.role || 'N/A'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    {employee.branches && employee.branches.length > 0 ? (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {employee.branches.slice(0, 2).map((branch) => (
                          <span
                            key={branch.id}
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: '#f0fdf4',
                              color: '#166534',
                              border: '1px solid #bbf7d0'
                            }}
                          >
                            {branch.branch_name}
                          </span>
                        ))}
                        {employee.branches.length > 2 && (
                          <span
                            style={{
                              padding: '2px 6px',
                              borderRadius: '4px',
                              fontSize: '11px',
                              backgroundColor: '#f3f4f6',
                              color: '#6b7280'
                            }}
                          >
                            +{employee.branches.length - 2} more
                          </span>
                        )}
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>No branches assigned</span>
                    )}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      backgroundColor: employee.is_active ? '#d1fae5' : '#fee2e2',
                      color: employee.is_active ? '#065f46' : '#991b1b'
                    }}>
                      {employee.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => openBranchModal(employee)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#4f46e5',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                        title="Manage Branch Assignments"
                      >
                        <MapPin size={14} />
                        Branches
                      </button>
                      {isSuperAdmin(user) && (
                        <>
                          <button
                            onClick={() => openModal(employee)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#f59e0b',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => toggleStatus(employee)}
                            style={{
                              padding: '6px 12px',
                              backgroundColor: employee.is_active ? '#ef4444' : '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            <UserCheck size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {loading && employees.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            Loading employees...
          </div>
        )}

        {!loading && employees.length === 0 && (
          <div style={{
            textAlign: 'center',
            padding: '40px',
            color: '#6b7280'
          }}>
            No employees found.
          </div>
        )}
      </div>

      {/* Employee Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
              </h2>
              <button onClick={closeModal} className="close-btn">
                ×
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="Enter employee name"
                  {...register('name', {
                    required: 'Name is required',
                    minLength: {
                      value: 2,
                      message: 'Name must be at least 2 characters'
                    }
                  })}
                />
                {errors.name && (
                  <div className="error-message">{errors.name.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="Enter email"
                  {...register('email', {
                    required: 'Email is required',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'Invalid email address'
                    }
                  })}
                />
                {errors.email && (
                  <div className="error-message">{errors.email.message}</div>
                )}
              </div>

              {!editingEmployee && (
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder="Enter password (min 8 characters)"
                    {...register('password', {
                      required: !editingEmployee && 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      }
                    })}
                  />
                  {errors.password && (
                    <div className="error-message">{errors.password.message}</div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Role *</label>
                <select
                  className="form-select"
                  {...register('role_id', { required: 'Role is required' })}
                >
                  <option value="">Select Role</option>
                  {roles.map((role) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {errors.role_id && (
                  <div className="error-message">{errors.role_id.message}</div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  {...register('is_active', { required: 'Status is required' })}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? 'Saving...' : editingEmployee ? 'Update Employee' : 'Create Employee'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Branch Assignment Modal */}
      {showBranchModal && assigningBranchTo && (
        <div className="modal-overlay" onClick={closeBranchModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <div className="modal-header">
              <h2 className="modal-title">
                Assign Branches - {assigningBranchTo.name}
              </h2>
              <button onClick={closeBranchModal} className="close-btn">
                ×
              </button>
            </div>

            <div style={{ padding: '24px', maxHeight: '70vh', overflowY: 'auto' }}>
              <p style={{ marginBottom: '16px', color: '#6b7280', fontSize: '14px' }}>
                Select the branches this employee will be assigned to:
              </p>

              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: '12px',
                marginBottom: '20px'
              }}>
                {branches.map((branch) => (
                  <label
                    key={branch.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      padding: '12px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor: selectedBranches.includes(branch.id) ? '#f0f9ff' : 'white',
                      borderColor: selectedBranches.includes(branch.id) ? '#3b82f6' : '#e5e7eb',
                      transition: 'all 0.2s'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedBranches.includes(branch.id)}
                      onChange={() => handleBranchToggle(branch.id)}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>{branch.branch_name}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280' }}>{branch.city}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'flex-end',
                marginTop: '24px'
              }}>
                <button
                  type="button"
                  onClick={closeBranchModal}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAssignBranches}
                  className="btn btn-primary"
                >
                  <MapPin size={16} style={{ marginRight: '8px' }} />
                  Assign Branches
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeMaster;
